/**
 * WorkflowSettingsDialog - Configurações do processo (Workflow 2.0)
 *
 * Permite rever e editar, depois da criação, os dados do processo:
 * nome, descrição, escopo por workspace e política padrão de bloqueio.
 * A "entidade principal" é exibida apenas para leitura, porque ela define
 * a raiz de todas as execuções — trocá-la invalidaria gatilhos, caminhos e
 * instâncias já existentes (a própria RPC `upsert_workflow_definition`
 * ignora esse campo na edição).
 *
 * Camada de apresentação: toda a escrita passa por `upsertDefinition`
 * (RPC segura), sem consulta direta a tabelas.
 *
 * Leiaute: usa `WorkflowDialogShell`, que segue o modelo do modal de
 * anotações do chat (header premium com gradiente + tile de ícone, corpo
 * rolável e footer compacto com botões `h-7`).
 *
 * @version 1.3.0
 * RELEASE: 2026-09-03 v1.3.0 - Nova seção "Versões" (somente leitura): lista
 *          todas as versões do processo em ordem decrescente, com etiqueta
 *          "Vigente" na versão em uso e "Rascunho" na versão de trabalho,
 *          facilitando identificação e manutenção (item 2 do pedido).
 *          Os dados já chegam na RPC de edição — nenhuma consulta adicional.
 * RELEASE: 2026-08-31 v1.2.1 - Sincronização dos campos passa a reagir às
 *          mudanças reais da definição (id/nome/descrição/bloqueio/workspaces),
 *          evitando exibir dados antigos após salvar.
 * RELEASE: 2026-08-31 v1.2.0 - Leiaute refatorado para `WorkflowDialogShell`

 *          (modelo do modal de anotações do chat): header premium com
 *          gradiente + tile de ícone, footer compacto `h-7`. Sem alteração
 *          de lógica/campos.
 * RELEASE: 2026-08-31 v1.1.0 - Leiaute padronizado com o sheet do construtor
 *          (cartões de seção, rótulos compactos e ajuda no botão "?") +
 *          corpo rolável e correção da rolagem interna da lista de workspaces
 * RELEASE: 2026-08-31 v1.0.0 - Criação inicial (edição do processo após criado)
 */

import { useEffect, useState } from 'react';
import { Loader2, Save, Settings2, X, GitBranch } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import { WorkflowDialogShell } from '@/components/workflow/WorkflowDialogShell';
import { WorkflowProcessFields } from '@/components/workflow/WorkflowProcessFields';
import { useCrmWorkspacesHierarchy } from '@/hooks/useCrmWorkspacesHierarchy';
import { useWorkflowOverview } from '@/hooks/useWorkflowDesigner';


/** Dados mínimos do processo necessários ao formulário */
export interface WorkflowSettingsDefinition {
  id: string;
  name: string;
  description: string | null;
  root_entity_type: string;
  default_enforcement?: string | null;
  workspace_ids?: string[] | null;
}

/** Resumo de uma versão do processo (vem pronto da RPC de edição) */
export interface WorkflowSettingsVersion {
  id: string;
  version_number: number;
  status: string;
  published_at: string | null;
}

/** Rótulo amigável do status da versão gravado no banco */
const VERSAO_STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  published: 'Publicada',
  archived: 'Arquivada',
};

/** Formata a data de publicação no padrão brasileiro (ou traço quando ausente) */
function formatarPublicacao(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

interface Props {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  definition: WorkflowSettingsDefinition;
  /** Versões já carregadas pelo construtor (exibidas na seção "Versões") */
  versions?: WorkflowSettingsVersion[];
  /** Id da versão em uso (recebe a etiqueta "Vigente") */
  activeVersionId?: string | null;
  /** Id da versão de trabalho atual (recebe a etiqueta "Rascunho") */
  draftVersionId?: string | null;
  /** Somente leitura quando o usuário não tem permissão de edição */
  podeEditar?: boolean;
  /** Chamado após salvar com sucesso (para recarregar o construtor) */
  onSaved?: () => void;
}

export function WorkflowSettingsDialog({
  open,
  onOpenChange,
  definition,
  versions,
  activeVersionId,
  draftVersionId,
  podeEditar = true,
  onSaved,
}: Props) {
  const { overview, upsertDefinition } = useWorkflowOverview();
  const { workspaces } = useCrmWorkspacesHierarchy();

  const [nome, setNome] = useState(definition.name);
  const [descricao, setDescricao] = useState(definition.description ?? '');
  const [enforcement, setEnforcement] = useState(definition.default_enforcement || 'allow');
  const [workspaceIds, setWorkspaceIds] = useState<string[]>(definition.workspace_ids ?? []);

  // Ao reabrir (ou quando a definição muda no cache), parte dos valores atuais.
  // As dependências olham os campos em si — e não a identidade do objeto —
  // para refletir corretamente os dados recém-salvos.
  useEffect(() => {
    if (!open) return;
    setNome(definition.name);
    setDescricao(definition.description ?? '');
    setEnforcement(definition.default_enforcement || 'allow');
    setWorkspaceIds(definition.workspace_ids ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    definition.id,
    definition.name,
    definition.description,
    definition.default_enforcement,
    (definition.workspace_ids ?? []).join(','),
  ]);


  /** Catálogo de entidades (usado só para exibir o rótulo, campo é read-only) */
  const entidades = overview?.entity_types ?? [];

  // Versões ordenadas da mais nova para a mais antiga (exibição na seção "Versões")
  const versoesOrdenadas = [...(versions ?? [])].sort((a, b) => b.version_number - a.version_number);


  /** Marca/desmarca um workspace do escopo */
  const alternarWorkspace = (id: string) =>
    setWorkspaceIds((atual) => (atual.includes(id) ? atual.filter((w) => w !== id) : [...atual, id]));

  const salvar = async () => {
    await upsertDefinition.mutateAsync({
      id: definition.id,
      name: nome.trim(),
      description: descricao.trim() || null,
      root_entity_type: definition.root_entity_type,
      workspace_ids: workspaceIds,
      default_enforcement: enforcement,
    });
    onOpenChange(false);
    onSaved?.();
  };

  return (
    <WorkflowDialogShell
      open={open}
      onOpenChange={onOpenChange}
      icon={Settings2}
      title="Configurações do processo"
      description="Dados gerais da esteira. O evento que faz o processo iniciar é configurado no bloco Início, dentro do construtor."
      footer={
        <>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 sm:px-3"
            onClick={() => onOpenChange(false)}
          >
            <X className="mr-1 h-4 w-4" /> Fechar
          </Button>
          <Button
            size="sm"
            className="h-7 px-2 sm:px-3"
            onClick={salvar}
            disabled={!podeEditar || !nome.trim() || upsertDefinition.isPending}
          >
            {upsertDefinition.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-1 h-4 w-4" />
            )}
            Salvar
          </Button>
        </>
      }
    >
      <WorkflowProcessFields
        nome={nome}
        onNomeChange={setNome}
        descricao={descricao}
        onDescricaoChange={setDescricao}
        entidade={definition.root_entity_type}
        entidades={entidades}
        enforcement={enforcement}
        onEnforcementChange={setEnforcement}
        workspaceIds={workspaceIds}
        onToggleWorkspace={alternarWorkspace}
        workspaces={workspaces ?? []}
        disabled={!podeEditar}
      />

      {/* Seção somente leitura: histórico de versões do processo.
          A versão "Vigente" é a que os negócios em andamento seguem; o
          "Rascunho" é a versão em edição no construtor. */}
      {versoesOrdenadas.length > 0 && (
        <section className="space-y-2 rounded-lg border border-border p-3">
          <header className="flex items-center gap-2">
            <GitBranch className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Versões
            </h3>
          </header>
          <ul className="divide-y divide-border">
            {versoesOrdenadas.map((v) => {
              const vigente = v.id === activeVersionId;
              const rascunho = v.id === draftVersionId;
              return (
                <li key={v.id} className="flex items-center justify-between gap-2 py-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="shrink-0 text-xs font-semibold text-foreground">
                      v{v.version_number}
                    </span>
                    <span className="truncate text-[11px] text-muted-foreground">
                      {VERSAO_STATUS_LABEL[v.status] ?? v.status}
                      {v.published_at && ` · Publicada em ${formatarPublicacao(v.published_at)}`}
                    </span>
                  </div>
                  {/* Etiquetas de identificação: vigente (em uso) e rascunho (em edição) */}
                  <div className="flex shrink-0 items-center gap-1">
                    {vigente && (
                      <Badge className="bg-success/10 text-success hover:bg-success/10">
                        Vigente
                      </Badge>
                    )}
                    {rascunho && (
                      <Badge variant="secondary">Rascunho</Badge>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </WorkflowDialogShell>
  );
}
